import _ from 'underscore';
import moment from 'moment';
import { GET, POST } from './HTTP';
import { DATE_FORMAT, GET_ENV_VARS_URL } from '../constants/appConstants';
import { SERVICE_URL as LOCAL_SERVICE_URL } from '../services/serviceUrl.local';

import TrackWindow from '../model/TrackWindow';

// module variable that contains the list of service urls 
// set within callback of retrieveEnvironmentVars
let SERVICE_URL;

const toHttpGetUrl = (rootUrl, params) => {
    const paramString = _.isEmpty(params) ? '' : new URLSearchParams(params);
    const url = rootUrl ? `${rootUrl}?${paramString}` : '/';

    return url;
}

/**
 * retrieve environment variables from OpenShift configMap
 * @returns Promise
 * @see https://wiki.bnsf.com/display/TW/Nginx+FAQs+For+EDP#NginxFAQsForEDP-CanIuseenvironmentspecificconfigurationforstaticcontentdeployment?
 */
export const retrieveEnvironmentVars = () => {
    if (process.env.NODE_ENV === 'production') {
        return GET(GET_ENV_VARS_URL).then(vars => SERVICE_URL = vars);
    } else {
        SERVICE_URL = LOCAL_SERVICE_URL;
        return Promise.resolve(SERVICE_URL);
    }
}

/**
 * check to see if the user is a FLS
 * @param {Object} userProfile 
 * @returns true if the user is a FLS; false otherwise
 */
export const isAuthorized = (userProfile) => {
    const req = {
        userId: userProfile ? userProfile.sub : 'dummy',
        authGroup: 'TIMEREVIEW9'
    }

    return POST(SERVICE_URL.IS_AUTHORIZED, JSON.stringify(req))
        .then(serviceResp => {
            if (serviceResp.returnCode === "0") {
                return serviceResp.isAuthorized;
            } else {
                throw Error('Failed authorization');
            }
        });
}

export const getDivisions = () => GET(SERVICE_URL.GET_DIVISIONS);

export const getSubdivisions = (divName) => {
    const requestBody = divName ? { division: divName } : {};
    const urlWithParams = toHttpGetUrl(SERVICE_URL.GET_SUBDIVISIONS, requestBody);

    return GET(urlWithParams);
}

export const getGangIds = () => GET(SERVICE_URL.GET_GANGIDS);
export const getRejectionCodes = () => GET(SERVICE_URL.GET_REJECTION_CODES);

export const getControlPointsByLSMP = (lsmp) => {
    return POST(SERVICE_URL.GET_CONTROLPOINT_BY_LSMP, JSON.stringify(lsmp));
}

/**
 * get the track list from gis-api GET /api/GetTrackInfoForLSMPRange (POST is denied by gis-api)
 * @returns Promise resolving to the legacy ENRailService shape { returnCode, trackInfoList }
 */
export const getTrackListByLSMP = (lsmp) => {
    const urlWithParams = toHttpGetUrl(SERVICE_URL.GET_TRACKLIST_BY_LSMP, {
        ...lsmp,
        bgnMpSfx: lsmp.bgnMpSfx || '',
        endMpSfx: lsmp.endMpSfx || ''
    });

    return GET(urlWithParams).then(serviceResp => ({
        returnCode: String(serviceResp.ReturnCode),
        trackInfoList: Array.isArray(serviceResp.Result) ? serviceResp.Result : []
    }));
}

export const createWindowRequest = (newWin, userProfile) => {
    const nw = newWin.toServiceRequest();
    const winService = Object.assign({}, nw, {
        requestorFN: userProfile.given_name,
        requestorLN: userProfile.family_name,
        comments: `${userProfile.given_name} ${userProfile.family_name} | ${nw.comments}`
    });
    
    const winReq = {
        userInfoModel: {
            userId: userProfile.sub,
            applnId: 'BWWPM'
        },
        twmmodelBWWP: [{
            trackWindowReq: winService
        }]
    };

    return POST(SERVICE_URL.CREATE_WINDOW, JSON.stringify(winReq));
}

export const copyWindowRequest = (winToCopy, datesToCopy, userProfile) => {
    let  wins = [];
    datesToCopy.forEach(aDate => {
        let newWin = Object.assign({}, winToCopy.toServiceRequest(), {
            winDt: aDate,
            requestorFN: userProfile.given_name,
            requestorLN: userProfile.family_name
        });

        if (newWin.trkWinReqId) {
            delete newWin.trkWinReqId;
        }

        wins.push({ trackWindowReq: newWin });
    });

    const winReq = {
        userInfoModel: {
            userId: userProfile.sub,
            applnId: 'BWWPM'
        },
        twmmodelBWWP: wins
    };

    return POST(SERVICE_URL.CREATE_WINDOW, JSON.stringify(winReq));
}

export const updateWindowRequest = (newWin, userProfile) => {
    const winService = Object.assign({}, newWin.toServiceRequest(), {
        requestorFN: userProfile.given_name,
        requestorLN: userProfile.family_name
    });
    
    const winReq = {
        userInfoModel: {
            userId: userProfile.sub,
            applnId: 'BWWPM'
        },
        twmmodelBWWP: [{
            trackWindowReq: winService
        }]
    };

    return POST(SERVICE_URL.UPDATE_WINDOW, JSON.stringify(winReq));
}

export const deleteWindowRequest = (winId, userProfile) => {
    const winReq = {
        userId: userProfile.sub,
        applnId: 'BWWPM',
        trkWinReqIds: [ winId ]
    };

    return POST(SERVICE_URL.DELETE_WINDOW, JSON.stringify(winReq));
}

export const getWindowRequestsByRequestorId = req => {
    const urlWithParams = toHttpGetUrl(SERVICE_URL.GET_WINDOWS_BY_OWNER, { 
        requestorId: req.requestorId,        
        startDate: (req.startDate || moment().format(DATE_FORMAT)) + 'T00:00:00.000Z',
        endDate: (req.endDate || req.startDate || moment().format(DATE_FORMAT)) + 'T23:59:59.999Z',
        windowStatus: req.windowStatus || ''
    });
    
    return GET(urlWithParams).then(serviceResp => {
        let wins = [];
        if (serviceResp && serviceResp.windowRequests) {
            serviceResp.windowRequests.forEach(aWin => {
                let newWin = new TrackWindow();
                newWin.fromQueryService(aWin);
                wins.push(newWin);
            });
        }
    
        return wins;
    });
}

/**
 * 
 * @param {Object} req Request body with below structure
 * {
 *      "division": "CA",
 *      "subdivision": "BAKERSFIELD",
 *      "startDate": "2020-01-01",
 *      "endDate": "2020-06-26",
 *      "gangIds": "CG01/CDW"
 *   }
 * @description gangIds='CG01/CDW' means to look for windows whose gangIds property contains either 
 * 'CG01' or 'CDW' or both.
 * @returns Promise to return the list of windows that meet the conditions included in req
 */
export const getWindowRequests = req => {
    const requestBody = {
        "division": req.division,
        "subdivisions": req.subdivisions,
        "startDate": (req.startDate || moment().format(DATE_FORMAT)) + 'T00:00:00.000Z',
        "endDate": (req.endDate || req.startDate || moment().format(DATE_FORMAT)) + 'T23:59:59.999Z',
        "gangIds": req.gangIds || null,
        "windowStatus": req.windowStatus || null
    };

    return POST(SERVICE_URL.GET_WINDOWS, JSON.stringify(requestBody))
        .then(serviceResp => {
            let wins = [];
            if (serviceResp && serviceResp.windowRequests) {
                serviceResp.windowRequests.forEach(aWin => {
                    let newWin = new TrackWindow();
                    newWin.fromQueryService(aWin);
                    wins.push(newWin);
                });
            }
        
            return wins;
        });
}

/**
 * Validate a window against engineering division guidelines
 * @param {Object} aWin model/TrackWindow object
 * @returns Promise
 */
export const validateWindowRequest = aWin => {
    const win = aWin.applySwitchYes();

    const winReq = {
        lineSegment: win.lineSegment,
        beginMilepost: win.bmp,
        endMilepost: win.emp,
        beginMilepostSuffix: win.bmpSuf,
        endMilepostSuffix: win.empSuf,
        windowStartDate: win.startDate,
        windowStartTime: win.startTime,
        windowType: win.winType,
        windowDuration: win.duration,
        trackType: win.trackType,
        trackNumber: win.trackNum,
        trackList: win.trackList,
        gangIds: win.gangIds
    };

    return POST(SERVICE_URL.GUIDELINE_VALIDATOR, JSON.stringify(winReq));

    // TODO: remove this mock response 
    // return Promise.resolve({
    //     windowValid: true
    // });
}

/**
 * Apply switch yes logic to a given window
 * @param {Object} win model/TrackWindow object
 * @returns Promise
 */
export const applySwitchYes = win => {
    const req = {
        lineSegment: win.lineSegment,
        beginMilepost: win.bmp,
        endMilepost: win.emp,
        beginMilepostSuffix: win.bmpSuf,
        endMilepostSuffix: win.empSuf,
        trackList: win.trackList
    };

    return POST(SERVICE_URL.APPLY_SWITCH_YES, JSON.stringify(req));
}

/**
 * fetch flocs agianst a window 
 * @param {Object} aWin model/TrackWindow object
 * @returns 
 */
 export const fetchWindowFlocs = aWin => {
    const win = aWin.applySwitchYes();

    const winReq = {
        lineSegment: win.lineSegment,
        beginMP: win.bmp,
        endMP: win.emp,
        beginMPSfx: win.bmpSuf,
        endMPSfx: win.empSuf,
        trackList: win.trackList
    };

    return POST(SERVICE_URL.FETCH_WINDOW_FLOCS, JSON.stringify(winReq));
}

/**
 * Identify transportation authority type for a window
 * @param {Object} aWin model/TrackWindow object
 * @note 99.9% of the network, authority type will be the same for all tracks when sharing the same LSMP
 * @returns 
 */
export const getAuthorityType = aWin => {
    const win = aWin.applySwitchYes();

    const req = {
        lineSegment: win.lineSegment,
        milepost: win.bmp,
        milepostSuffix: win.bmpSuf
    };

    return POST(SERVICE_URL.GET_AUTHORITY_TYPE_BY_LOCATION, JSON.stringify(req));
}
